import React, { useEffect, useMemo, useState } from 'react';
import { LifeBuoyIcon, RefreshCwIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { LaunchStepCard } from '../components/launch/LaunchStepCard';
import { getLaunchProgress, launchSteps, launchTruths, maintenanceRhythm, maintenanceTasks, saveLaunchProgress, troubleshooting, type LaunchContext } from '../data/launch';
import { getPublishedWebsiteContent, websiteUpdatedEvent, type WebsiteContent } from '../data/website';
import { getIntegrationConnections, integrationsUpdatedEvent, type IntegrationConnection } from '../data/integrations';
import { getTeamMembers, type TeamMember } from '../data/teamAccess';
export function LaunchPlan() {
  const [content, setContent] = useState<WebsiteContent>(() => getPublishedWebsiteContent());
  const [connections, setConnections] = useState<IntegrationConnection[]>(() => getIntegrationConnections());
  const [team] = useState<TeamMember[]>(() => getTeamMembers());
  const [completed, setCompleted] = useState<string[]>(() => getLaunchProgress());
  useEffect(() => {
    const refreshContent = () => setContent(getPublishedWebsiteContent());
    const refreshConnections = () => setConnections(getIntegrationConnections());
    window.addEventListener(websiteUpdatedEvent, refreshContent);
    window.addEventListener(integrationsUpdatedEvent, refreshConnections);
    return () => {
      window.removeEventListener(websiteUpdatedEvent, refreshContent);
      window.removeEventListener(integrationsUpdatedEvent, refreshConnections);
    };
  }, []);
  useEffect(() => {
    saveLaunchProgress(completed);
  }, [completed]);
  const context: LaunchContext = useMemo(() => ({
    content,
    connections,
    team
  }), [content, connections, team]);
  const toggle = (id: string) => setCompleted(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);
  const doneCount = completed.length;
  const percent = Math.round(doneCount / launchSteps.length * 100);
  const nextStep = launchSteps.find(step => !completed.includes(step.id));
  return <div data-id="element-1595" data-mp="kb7hlfe1ysb3">
      <PageHeader eyebrow="Launch" title="Taking coopertyband.com live." subtitle="Eight steps, in order, from filling in your content to retiring Bandzoogle. Nothing here requires code — the longest part is waiting for the domain to switch over." data-id="element-1596" data-mp="kbzectjdyw2y" />

      <section className="mb-6" aria-labelledby="launch-progress" data-id="element-1597" data-mp="3it3czfltmn3r">
        <Card className="p-5" data-id="element-1598" data-mp="3iuwi2qgscz9n">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1599" data-mp="1clfpyapox64">
            <div data-id="element-1600" data-mp="1eoh1ehbecq5">
              <h2 id="launch-progress" className="text-[17px] font-semibold text-white" data-id="element-1601" data-mp="3sr9dbsrzbl6i">
                {doneCount === launchSteps.length ? 'Every step is done.' : `Step ${doneCount + 1} of ${launchSteps.length}`}
              </h2>
              <p className="mt-1 max-w-lg text-[12.5px] leading-relaxed text-zinc-500" data-id="element-1602" data-mp="303deahexth6k">
                {nextStep ? `Up next: ${nextStep.title.toLowerCase()}. ${nextStep.timing.toLowerCase()}.` : 'Keep Bandzoogle paid for one more week, then cancel with your mailing list safely exported.'}
              </p>
            </div>
            <span className="text-[26px] font-semibold tabular-nums leading-none tracking-tight text-white" data-id="element-1603" data-mp="1algahubab8m1">
              {percent}%
            </span>
          </div>
          <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.07]" data-id="element-1604" data-mp="1ybu43p9ipfiq">
            <div className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out" style={{
            width: `${percent}%`
          }} data-id="element-1605" data-mp="239lwslhzl5nz" />
          </div>
        </Card>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-id="element-1606" data-mp="1ubjxqbhtcn3c">
        <div className="space-y-6" data-id="element-1607" data-mp="dueek1x0spsa">
          <section aria-labelledby="launch-steps" data-id="element-1608" data-mp="dxk0j8acrbjs">
            <h2 id="launch-steps" className="sr-only" data-id="element-1609" data-mp="ivbwcde4bht9">
              Launch steps
            </h2>
            <div className="space-y-3" data-id="element-1610" data-mp="y4yyuhuqownp">
              {launchSteps.map(step => <LaunchStepCard key={step.id} step={step} context={context} done={completed.includes(step.id)} onToggle={() => toggle(step.id)} data-id="element-1611" data-mp="tcytg9vwhbjq" />)}
            </div>
          </section>

          <section aria-labelledby="launch-maintenance" data-id="element-1612" data-mp="1h1qgynyjp5q6">
            <div className="mb-3" data-id="element-1613" data-mp="1c3oz26cuvcga">
              <h2 id="launch-maintenance" className="flex items-center gap-2 text-[16px] font-semibold text-white" data-id="element-1614" data-mp="1c3glsdctm54k">
                <RefreshCwIcon className="h-4 w-4 text-accent-soft" data-id="element-1615" data-mp="180s9twst8i3d" /> Keeping the site up to date
              </h2>
              <p className="mt-0.5 text-[12.5px] text-zinc-500" data-id="element-1616" data-mp="132qthjmrqwvl">
                Once you are live, almost everything happens in one of two places.
              </p>
            </div>
            <Card className="divide-y divide-white/[0.05]" data-id="element-1617" data-mp="3nsg8pxrdwkot">
              {maintenanceTasks.map(task => <div key={task.trigger} className="grid gap-1 p-4 sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-4" data-id="element-1618" data-mp="23crrnpa0ghxy">
                  <div className="text-[13px] font-medium text-white" data-id="element-1619" data-mp="288772lno2lwh">{task.trigger}</div>
                  <div className="text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1620" data-mp="2calnz07rzbl4">{task.action}</div>
                </div>)}
            </Card>
            <Card className="mt-3 p-4" data-id="element-1621" data-mp="2m27xms4gt2lr">
              <h3 className="text-[13.5px] font-semibold text-white" data-id="element-1622" data-mp="2m16o4s33ko65">A simple rhythm</h3>
              <ul className="mt-2.5 space-y-1.5" data-id="element-1623" data-mp="2qzhxz2te6yod">
                {maintenanceRhythm.map(item => <li key={item} className="flex gap-2.5 text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1624" data-mp="2w0dvmyn5holc">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-accent" data-id="element-1625" data-mp="300xwac6xnac8" />
                    {item}
                  </li>)}
              </ul>
            </Card>
          </section>

          <section aria-labelledby="launch-troubleshooting" data-id="element-1626" data-mp="15pj3fmimkus9">
            <div className="mb-3" data-id="element-1627" data-mp="28zf1bajy9y34">
              <h2 id="launch-troubleshooting" className="flex items-center gap-2 text-[16px] font-semibold text-white" data-id="element-1628" data-mp="290gfvp06d2pl">
                <LifeBuoyIcon className="h-4 w-4 text-accent-soft" data-id="element-1629" data-mp="2hz27sarjdu31" /> If something goes wrong
              </h2>
            </div>
            <Card className="divide-y divide-white/[0.05]" data-id="element-1630" data-mp="2dx75b2xvdano">
              {troubleshooting.map(item => <div key={item.problem} className="p-4" data-id="element-1631" data-mp="2rw6k7ivkyb6n">
                  <div className="text-[13px] font-medium text-white" data-id="element-1632" data-mp="2my51iz22gdvp">{item.problem}</div>
                  <p className="mt-1 text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1633" data-mp="2mwaos80y7s2p">{item.fix}</p>
                </div>)}
            </Card>
          </section>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start" data-id="element-1634" data-mp="2woq4szsoq6ky">
          <Card className="p-4" data-id="element-1635" data-mp="3aohggtvqbavs">
            <h3 className="text-[13.5px] font-semibold text-white" data-id="element-1636" data-mp="3almyra43o462">Before you start</h3>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1637" data-mp="35qfy6b64nfld">
              Do not cancel Bandzoogle until the new site is confirmed live. Keep it paid through launch week — if
              anything goes wrong with the domain you can point it back in minutes, but only while that account is
              still active.
            </p>
          </Card>

          {launchTruths.map(truth => <Card key={truth.heading} className="p-4" data-id="element-1638" data-mp="3kd6ae7le2slq">
              <h3 className="text-[13.5px] font-semibold text-white" data-id="element-1639" data-mp="36ivqq4o91omj">{truth.heading}</h3>
              <ul className="mt-2.5 space-y-1.5" data-id="element-1640" data-mp="3bawypeedwt6r">
                {truth.items.map(item => <li key={item} className="flex gap-2.5 text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1641" data-mp="2wmiqeny78cit">
                    <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-white/25" data-id="element-1642" data-mp="31lvltx34yrx0" />
                    {item}
                  </li>)}
              </ul>
            </Card>)}

          <Card className="p-4" data-id="element-1643" data-mp="2sj49zw6clu4u">
            <h3 className="text-[13.5px] font-semibold text-white" data-id="element-1644" data-mp="2sm8hp3j0v809">How content is stored today</h3>
            <p className="mt-1.5 text-[12.5px] leading-relaxed text-zinc-400" data-id="element-1645" data-mp="3802s5dvj5fip">
              Your published site content lives in the browser you edit from. That is fine for launch, but edits made
              on your laptop are not automatically there if you open the studio on your phone. Do your editing from one
              main computer until a database is added.
            </p>
          </Card>
        </aside>
      </div>
    </div>;
}